import {
  Box,
  IconButton,
  Button,
  Collapse,
  Grid,
  Divider,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DatePicker from "../shared/datePicker";
import { useState, useEffect, useMemo } from "react";
import SingleAutocomplete from "../shared/singleAutocomplete";
import { Option } from "@/app/interfaces/Option";
import { useOrderHistoryFilters } from "@/app/hooks/useOrderHistoryFilter";
import OrderHistoryFilterState from "@/app/interfaces/OrderHistoryFilter";
import { OrderHistoryFilterFieldEnum } from "@/enums/orderHistoryFilterFiledEnum";
import FormTextField from "../shared/formTextField";
import { PICKUP_DELIVERY_OPTIONS } from "@/app/options/OrderHistoryFiltersOptions";
import { Dayjs } from "dayjs";
import PaginatedMultiAutocomplete from "../shared/paginatedMultiAutocomplete";
import FetchLookupOptionsHelper from "@/app/helpers/FetchLookupOptionsHelper";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "@/app/constants/constants";
import { LookupTypesEnum } from "@/enums/lookupTypesEnum";
import FilterHelper from "@/app/helpers/FilterHelper";
import { useLocation, useNavigate } from "react-router-dom";
import UrlHelper from "@/app/helpers/UrlHelper";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import {
  clearFromStorage,
  clearOrdersFromStorage,
  loadFromStorage,
  saveToStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";
import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import { omit } from "lodash";
import { StatusGroupEnum } from "@/enums/StatusGroupEnum";
import { STATUS_GROUPS } from "./OrderHistoryHeaderFilters";
import BorderedInputLansing from "@/app/components/shared/BorderedInput";

const OrderAdvancedFilters = () => {
  const { search } = useLocation();
  const hasShipTos = useSelector(
    (state: RootState) => state.userState.user.accountMenu?.shipToVisible
  );

  const defaultShipTo = useSelector(
    (state: RootState) => state.userState.user.defaultShipTo
  );

  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";

  const clearedState = {
    orderFilter: "",
    orderNumber: "",
    jobName: "",
    itemNumber: "",
    itemDescription: "",
    startDate: null,
    endDate: null,
    invoiceStartDate: null,
    invoiceEndDate: null,
    manufacturers: [],
    orderSubStatusIds: [],
    orderShipTos: [],
    categoryIds: [],
    pickupInStore: null,
  };

  const {
    filters,
    updateFilter,
    updateMultipleFilters,
    incrementRetriggerKey,
    activeGroup,
    setActiveGroup,
  } = useOrderHistoryFilters();
  const [filtersState, setFiltersState] =
    useState<OrderHistoryFilterState>(filters);

  const { tabKey, tabValue } = useMemo(() => {
    const params = new URLSearchParams(search);
    const tabKey = Array.from(params.keys())[0];
    const tabValue = params.get(tabKey);

    return { params, tabKey, tabValue };
  }, [search]);

  const setField = <K extends keyof OrderHistoryFilterState>(
    key: K,
    val: OrderHistoryFilterState[K]
  ) => setFiltersState((f) => ({ ...f, [key]: val }));

  const handleMultiAutocompleteChange = (
    name: keyof OrderHistoryFilterState,
    newValue: Option[]
  ) => {
    setField(name, newValue);
  };

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setField(OrderHistoryFilterFieldEnum.START_DATE, filters.startDate);
    setField(OrderHistoryFilterFieldEnum.END_DATE, filters.endDate);
  }, [filters.startDate, filters.endDate, userId]);

  useEffect(() => {
    if (tabValue === "orders" || tabValue === "quotes") {
      clearOrdersFromStorage(userId);
      const finalState = buildNextFiltersState(filtersState, activeGroup);
      setFiltersState(finalState);
      return;
    }

    const advancedSearch = loadFromStorage<boolean>(
      userId,
      STORAGE_KEYS.ORDER_ADVANCED_SEARCH
    );

    const payload = loadFromStorage<OrderHistoryFilterState>(
      userId,
      STORAGE_KEYS.ORDER_FILTERS
    );

    if (payload) {
      const nextState = {
        ...payload,
        advancedSearchExpanded: advancedSearch ?? false,
      };

      setFiltersState(nextState);
      updateMultipleFilters(nextState);
    }
  }, [updateMultipleFilters, userId]);

  useEffect(() => {
    const next = buildNextFiltersState(filtersState, activeGroup);
    setFiltersState((prev) => ({
      ...(tabValue === "orders" || tabValue === "quotes" ? next : prev),
      orderStatusIds: filters.orderStatusIds,
      typeIds: filters.typeIds,
      ...(tabKey === "orders" && tabValue
        ? { orderNumber: tabValue, advancedSearchExpanded: true }
        : undefined),
    }));
    if (tabValue === "orders" || tabValue === "quotes") {
      clearOrdersFromStorage(userId);
      updateFilter(OrderHistoryFilterFieldEnum.ORDER_FILTER, "");
    }
    if (tabKey === "orders") {
      updateFilter(OrderHistoryFilterFieldEnum.ADVANCED_SEARCH_EXPANDED, true);
    }
  }, [filters.orderStatusIds, filters.typeIds, tabKey, tabValue, updateFilter]);

  const handleSingleAutocompleteChange = (
    name: keyof OrderHistoryFilterState,
    newValue: Option | null
  ) => {
    setField(
      name,
      newValue ? (newValue.value as typeof filtersState.pickupInStore) : null
    );
  };

  const handleDateFieldChange = (
    name: keyof OrderHistoryFilterState,
    value: Dayjs | null
  ) => {
    setField(name, value?.format() ?? null);
  };

  const clearAll = () => {
    UrlHelper.clearAllSearchParams(navigate, location);
    clearOrdersFromStorage(userId);
    const clearedBase = {
      ...clearedState,
      orderStatusIds: [],
      typeIds: [],
    };

    setFiltersState((f) => ({
      ...f,
      ...clearedBase,
    }));
    updateMultipleFilters(clearedBase);
    clearFromStorage(userId, STORAGE_KEYS.ORDER_FILTERS);
    clearFromStorage(userId, STORAGE_KEYS.ORDER_ADVANCED_SEARCH);
    setActiveGroup(StatusGroupEnum.ALL);
  };

  const handleSearch = () => {
    UrlHelper.clearAllSearchParams(navigate, location);
    const filtersToSubmit = omit(filtersState, [
      OrderHistoryFilterFieldEnum.RETRIGGER_KEY,
    ]);
    updateMultipleFilters(filtersToSubmit);
    incrementRetriggerKey();
  };

  const onAdvancedSearchHandler = () => {
    const isExpanding = !filtersState.advancedSearchExpanded;
    saveToStorage(userId, STORAGE_KEYS.ORDER_ADVANCED_SEARCH, isExpanding);

    if (isExpanding) {
      const filterState = {
        ...filtersState,
        advancedSearchExpanded: true,
        orderFilter: "",
      };
      setFiltersState(filterState);
      updateMultipleFilters(filterState);
    } else {
      const nextFilters = buildNextFiltersState(filtersState, activeGroup);
      const finalState = {
        ...nextFilters,
        advancedSearchExpanded: false,
      };

      setFiltersState(finalState);
      updateMultipleFilters(finalState);

      clearFromStorage(userId, STORAGE_KEYS.ORDER_FILTERS);
      UrlHelper.clearAllSearchParams(navigate, location);
      incrementRetriggerKey();
    }
  };

  const buildNextFiltersState = (
    filtersState: OrderHistoryFilterState,
    activeGroup: StatusGroupEnum
  ) => {
    const next = {
      ...filtersState,
      ...clearedState,
    };

    if (activeGroup !== StatusGroupEnum.ALL) {
      const allowed = STATUS_GROUPS[activeGroup];
      next.orderStatusIds = filtersState.orderStatusIds.filter((x) =>
        allowed.has(Number(x.value))
      );
    } else {
      next.orderStatusIds = [];
    }

    if (activeGroup === StatusGroupEnum.QUOTED) {
      next.typeIds = [{ value: "Quote", text: "Quote", isFlagged: null }];
    } else {
      next.typeIds = [];
    }
    return next;
  };

  const createLookupFetcher =
    (lookupType: LookupTypesEnum, pageSize = PAGINATION_PAGE_SIZE_OPTIONS[0]) =>
    (query: string, page: number, signal?: AbortSignal, filter?: string[]) =>
      FetchLookupOptionsHelper.fetchLookupOptions({
        lookupType,
        search: query,
        page,
        pageSize,
        signal,
        filter,
      });

  const fetchManufacturers = useMemo(
    () => createLookupFetcher(LookupTypesEnum.MANUFACTURERS),
    []
  );

  const fetchOrderStatuses = useMemo(
    () => createLookupFetcher(LookupTypesEnum.ORDER_STATUSES),
    []
  );

  const fetchOrderSubStatuses = useMemo(
    () => (query: string, page: number, signal?: AbortSignal) =>
      createLookupFetcher(LookupTypesEnum.ORDER_SUB_STATUSES)(
        query,
        page,
        signal,
        FilterHelper.extractIdFromOptions(
          filtersState.orderStatusIds
        ) as string[]
      ),
    [filtersState.orderStatusIds]
  );

  const fetchOrderTypes = useMemo(
    () => createLookupFetcher(LookupTypesEnum.ORDER_TYPES),
    []
  );

  const fetchShipTos = useMemo(
    () => createLookupFetcher(LookupTypesEnum.SHIPS_TOS),
    []
  );

  const fetchCategories = useMemo(
    () => createLookupFetcher(LookupTypesEnum.CATEGORIES),
    []
  );

  return (
    <Box sx={{ p: 3, borderRadius: 3, mx: 2, backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: { xs: "100%", sm: "80%", md: "65%" } }}>
            <FormTextField
              fullWidth
              name="orderFilter"
              placeholder="Search Order Number or Job Name"
              value={filtersState.orderFilter || ""}
              onChange={(v) =>
                setField(OrderHistoryFilterFieldEnum.ORDER_FILTER, v)
              }
              clearable
              disabled={filtersState.advancedSearchExpanded}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              endAdornment={
                <IconButton
                  color="primary"
                  disabled={filtersState.advancedSearchExpanded}
                  onClick={handleSearch}
                >
                  <SearchIcon />
                </IconButton>
              }
            />
          </Box>
        </Box>
        <Button
          size="small"
          variant="text"
          sx={{ textTransform: "none", fontSize: "14px" }}
          startIcon={
            filtersState.advancedSearchExpanded ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )
          }
          onClick={onAdvancedSearchHandler}
        >
          Advanced Search
        </Button>
      </Box>
      <Collapse in={filtersState.advancedSearchExpanded} unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Order #:
              </Typography>
              <BorderedInputLansing
                name="orderNumber"
                value={filtersState.orderNumber || ""}
                onChange={(event) =>
                  setField(
                    OrderHistoryFilterFieldEnum.ORDER_NUMBER,
                    event.target.value
                  )
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Job Name:
              </Typography>
              <BorderedInputLansing
                name="jobName"
                value={filtersState.jobName || ""}
                onChange={(event) =>
                  setField(
                    OrderHistoryFilterFieldEnum.JOB_NAME,
                    event.target.value
                  )
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
                >
                  Order Start Date:
                </Typography>

                <DatePicker
                  label=""
                  name="orderDateStart"
                  defaultValue={filtersState.startDate}
                  value={filtersState.startDate}
                  onChange={(value) =>
                    handleDateFieldChange(
                      OrderHistoryFilterFieldEnum.START_DATE,
                      value
                    )
                  }
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
                >
                  Order End Date:
                </Typography>
                <DatePicker
                  label=""
                  name="orderDateEnd"
                  defaultValue={filtersState.endDate}
                  value={filtersState.endDate}
                  onChange={(value) =>
                    handleDateFieldChange(
                      OrderHistoryFilterFieldEnum.END_DATE,
                      value
                    )
                  }
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Item #:
              </Typography>
              <BorderedInputLansing
                name="itemNumber"
                value={filtersState.itemNumber || ""}
                onChange={(event) =>
                  setField(
                    OrderHistoryFilterFieldEnum.ITEM_NUMBER,
                    event.target.value
                  )
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Item Description:
              </Typography>
              <BorderedInputLansing
                name="itemDescription"
                value={filtersState.itemDescription || ""}
                onChange={(event) =>
                  setField(
                    OrderHistoryFilterFieldEnum.ITEM_DESCRIPTION,
                    event.target.value
                  )
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Manufacturer:
              </Typography>

              <PaginatedMultiAutocomplete
                label=""
                name="Manufacturer"
                value={filtersState.manufacturers}
                queryKey={OrderHistoryFilterFieldEnum.MANUFACTURERS}
                fetchFn={fetchManufacturers}
                onChange={(_, newValue) =>
                  handleMultiAutocompleteChange(
                    OrderHistoryFilterFieldEnum.MANUFACTURERS,
                    newValue
                  )
                }
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Order Status:
              </Typography>

              <PaginatedMultiAutocomplete
                label=""
                name="orderStatus"
                value={filtersState.orderStatusIds}
                queryKey={OrderHistoryFilterFieldEnum.ORDER_STATUS_IDS}
                fetchFn={fetchOrderStatuses}
                onChange={(_, newValue: Option[]) =>
                  handleMultiAutocompleteChange(
                    OrderHistoryFilterFieldEnum.ORDER_STATUS_IDS,
                    newValue
                  )
                }
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Order Sub Status:
              </Typography>

              <PaginatedMultiAutocomplete
                label=""
                name="orderSubStatus"
                value={filtersState.orderSubStatusIds}
                queryKey={`${
                  OrderHistoryFilterFieldEnum.ORDER_SUB_STATUS_IDS
                }_${FilterHelper.extractIdFromOptions(
                  filtersState.orderStatusIds
                ).join(",")}`}
                fetchFn={fetchOrderSubStatuses}
                onChange={(_, newValue: Option[]) =>
                  handleMultiAutocompleteChange(
                    OrderHistoryFilterFieldEnum.ORDER_SUB_STATUS_IDS,
                    newValue
                  )
                }
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Order Type:
              </Typography>

              <PaginatedMultiAutocomplete
                label=""
                name="orderType"
                value={filtersState.typeIds}
                queryKey={OrderHistoryFilterFieldEnum.TYPE_IDS}
                fetchFn={fetchOrderTypes}
                onChange={(_, newValue: Option[]) =>
                  handleMultiAutocompleteChange(
                    OrderHistoryFilterFieldEnum.TYPE_IDS,
                    newValue
                  )
                }
                size="small"
              />
            </Box>
          </Grid>
          {hasShipTos && defaultShipTo && (
            <Grid item xs={12} md={4}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
                >
                  Ship To:
                </Typography>

                <PaginatedMultiAutocomplete
                  label=""
                  name="shipTo"
                  value={filtersState.orderShipTos}
                  queryKey={OrderHistoryFilterFieldEnum.ORDER_SHIP_TOS}
                  fetchFn={fetchShipTos}
                  onChange={(_, newValue: Option[]) =>
                    handleMultiAutocompleteChange(
                      OrderHistoryFilterFieldEnum.ORDER_SHIP_TOS,
                      newValue
                    )
                  }
                  size="small"
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Pickup/Delivery:
              </Typography>

              <SingleAutocomplete
                label=""
                name="pickupDelivery"
                value={
                  PICKUP_DELIVERY_OPTIONS.find(
                    (o) => o.value === filtersState.pickupInStore
                  ) || null
                }
                options={PICKUP_DELIVERY_OPTIONS}
                onChange={(_, newValue) =>
                  handleSingleAutocompleteChange(
                    OrderHistoryFilterFieldEnum.PICKUP_IN_STORE,
                    newValue
                  )
                }
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
                >
                  Invoice Start Date:
                </Typography>
                <DatePicker
                  label=""
                  name="invoiceDateStart"
                  value={filtersState.invoiceStartDate}
                  onChange={(value) =>
                    handleDateFieldChange(
                      OrderHistoryFilterFieldEnum.INVOICE_START_DATE,
                      value
                    )
                  }
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
                >
                  Invoice End Date:
                </Typography>
                <DatePicker
                  label=""
                  name="invoiceDateEnd"
                  value={filtersState.invoiceEndDate}
                  onChange={(value) =>
                    handleDateFieldChange(
                      OrderHistoryFilterFieldEnum.INVOICE_END_DATE,
                      value
                    )
                  }
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Category:
              </Typography>

              <PaginatedMultiAutocomplete
                label=""
                name="category"
                value={filtersState.categoryIds}
                queryKey={OrderHistoryFilterFieldEnum.CATEGORY_IDS}
                fetchFn={fetchCategories}
                onChange={(_, newValue: Option[]) =>
                  handleMultiAutocompleteChange(
                    OrderHistoryFilterFieldEnum.CATEGORY_IDS,
                    newValue
                  )
                }
                size="small"
              />
            </Box>
          </Grid>
          {!hasShipTos && <Grid item xs={12} md={4} />}

          <Grid item xs={12} md={12}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                mt: { xs: 1, md: 0 },
              }}
            >
              <Button variant="outlined" onClick={clearAll}>
                CLEAR
              </Button>
              <Button variant="contained" onClick={handleSearch}>
                SEARCH
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
};

export default OrderAdvancedFilters;
